"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { User as UserIcon, ChevronDown, Plane, Heart, MessageSquare, Settings, LogOut } from "lucide-react";
import type { User } from "@/types";
import { supabase } from "@/lib/supabase";

interface UserProfileMenuProps {
  user: User;
}

export default function UserProfileMenu({ user }: UserProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleSignOut = async () => {
    // In test mode, clear test session cookie
    const isTestMode = process.env.NEXT_PUBLIC_TEST_MODE === 'true';
    if (isTestMode) {
      // Clear test session cookie by setting it to expire
      document.cookie = 'test_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    } else {
      // Sign out from Supabase
      await supabase.auth.signOut();
    }
    
    // Clear auth token
    localStorage.removeItem("auth_token");
    
    // Close menu and redirect to home page
    setIsOpen(false);
    router.push("/");
  };

  const normalizedRole = user?.role?.toLowerCase() || null;
  const isCustomer = normalizedRole === "customer";
  const isVendor = normalizedRole === "vendor";
  const isAdmin = normalizedRole === "admin";

  // Menu items for customers
  const customerMenuItems = [
    { icon: Plane, label: "My Trips", href: "/dashboard/trips" },
    { icon: Heart, label: "Favorites", href: "/dashboard/favorites" },
    { icon: MessageSquare, label: "Messages", href: "/messages" },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors hover:opacity-90"
        style={{
          borderColor: "rgba(212, 175, 55, 0.3)",
          backgroundColor: "rgba(212, 175, 55, 0.1)",
        }}
      >
        <UserIcon
          className="h-3.5 w-3.5"
          style={{ color: "#D4AF37" }}
        />
        <span
          className="text-xs normal-case tracking-normal max-w-[120px] sm:max-w-[200px] truncate"
          style={{ color: "#F8F5E9", fontFamily: "var(--font-sans)" }}
        >
          {user.name || user.email}
        </span>
        <ChevronDown
          className={`h-3 w-3 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
          style={{ color: "#D4AF37" }}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border z-50 py-1"
          style={{ borderColor: "rgba(10, 55, 64, 0.1)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Customer menu items */}
          {isCustomer && (
            <>
              {customerMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 text-sm transition-all rounded-lg mx-1"
                    style={{ color: "var(--pnn-text-dark)" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(212, 175, 55, 0.1)";
                      e.currentTarget.style.color = "var(--pnn-green)";
                      const icon = e.currentTarget.querySelector("svg");
                      if (icon) icon.style.color = "var(--pnn-gold)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "var(--pnn-text-dark)";
                      const icon = e.currentTarget.querySelector("svg");
                      if (icon) icon.style.color = "var(--pnn-green)";
                    }}
                  >
                    <Icon className="w-4 h-4 transition-colors" style={{ color: "var(--pnn-green)" }} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              <div className="border-t my-1" style={{ borderColor: "rgba(10, 55, 64, 0.1)" }} />
            </>
          )}

          {/* Vendor/Admin dashboard link */}
          {(isVendor || isAdmin) && (
            <>
              <Link
                href={isAdmin ? "/admin" : "/dashboard"}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-sm transition-all rounded-lg mx-1"
                style={{ color: "var(--pnn-text-dark)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(212, 175, 55, 0.1)";
                  e.currentTarget.style.color = "var(--pnn-green)";
                  const icon = e.currentTarget.querySelector("svg");
                  if (icon) icon.style.color = "var(--pnn-gold)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "var(--pnn-text-dark)";
                  const icon = e.currentTarget.querySelector("svg");
                  if (icon) icon.style.color = "var(--pnn-green)";
                }}
              >
                <Settings className="w-4 h-4 transition-colors" style={{ color: "var(--pnn-green)" }} />
                <span>{isAdmin ? "Admin Dashboard" : "Vendor Dashboard"}</span>
              </Link>
              <div className="border-t my-1" style={{ borderColor: "rgba(10, 55, 64, 0.1)" }} />
            </>
          )}

          {/* Support link (for all users) */}
          <Link
            href="/support"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2 text-sm transition-all rounded-lg mx-1"
            style={{ color: "var(--pnn-text-dark)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(212, 175, 55, 0.1)";
              e.currentTarget.style.color = "var(--pnn-green)";
              const icon = e.currentTarget.querySelector("svg");
              if (icon) icon.style.color = "var(--pnn-gold)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--pnn-text-dark)";
              const icon = e.currentTarget.querySelector("svg");
              if (icon) icon.style.color = "var(--pnn-green)";
            }}
          >
            <MessageSquare className="w-4 h-4 transition-colors" style={{ color: "var(--pnn-green)" }} />
            <span>Contact Support</span>
          </Link>

          {/* Profile settings link (for all users) */}
          <Link
            href="/profile"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2 text-sm transition-all rounded-lg mx-1"
            style={{ color: "var(--pnn-text-dark)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(212, 175, 55, 0.1)";
              e.currentTarget.style.color = "var(--pnn-green)";
              const icon = e.currentTarget.querySelector("svg");
              if (icon) icon.style.color = "var(--pnn-gold)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--pnn-text-dark)";
              const icon = e.currentTarget.querySelector("svg");
              if (icon) icon.style.color = "var(--pnn-green)";
            }}
          >
            <Settings className="w-4 h-4 transition-colors" style={{ color: "var(--pnn-green)" }} />
            <span>Profile Settings</span>
          </Link>

          {/* Sign Out */}
          <div className="border-t my-1" style={{ borderColor: "rgba(10, 55, 64, 0.1)" }} />
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-all rounded-lg mx-1 text-left"
            style={{ color: "#dc2626" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(220, 38, 38, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <LogOut className="w-4 h-4 transition-colors" />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
}

